(ns app.domain.bond.model
  (:require [app.domain.business.model :as business-model]
            [app.domain.group.model :as group-model]
            [app.domain.party.model :as party-model]
            [app.domain.util :as du]
            [app.domain.workspace.model :as workspace-model]
            [app.i18n.model :as i18n-model]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.db.child-entity :as child]
            [core.db.entity.util :as eu]
            [core.permission.resolver :as permission-resolver]
            [core.user.model :as user-model]))


(def ^{:arglists '([domain-group])} bonds-in-group
  (child/child-entities-of-type-fn :entity.type/bond))

(defn bonds-in-workspace-domain-group
  [workspace]
  (-> workspace
      :domain/authority-group
      :entity/parent
      bonds-in-group))

(defn bond-owned-by-workspace-party?
  [bond workspace]
  (let [workspace-parties (->> (party-model/workspace-parties workspace)
                               (mapcat (juxt :party/user :party/business))
                               (remove nil?)
                               (map :db/id)
                               set)
        bond-owner-id     (-> bond :bond/owner :db/id)]
    (contains? workspace-parties bond-owner-id)))

(def bond-states-valid-for-association
  #{:bond.state/verified
    :bond.state/unverified})

(def editable-bond-template-states
  #{:bond-template.state/active
    :bond-template.state/disabled})

(defn bonds-available-to-associate
  "Bonds that are owned by parties in workspace, not already associated with the workspace,
   in a state valid for association, and modifiable by the user."
  [user workspace]
  (when (group-model/has-feature? (:domain/authority-group workspace)
                                  :group.feature/use-bond-accounts)
    (set/difference
      (->> workspace
           bonds-in-workspace-domain-group
           (filter (fn [bond]
                     (and (bond-owned-by-workspace-party? bond workspace)
                          (permission-resolver/permitted? bond user :permission.bond/modify)
                          (contains? bond-states-valid-for-association (:domain/state bond)))))
           set)
      (-> workspace
          :workspace/bonds
          set))))

(defn can-edit-bond-attachments?
  "Check if a user can edit bond attachments."
  [bond user]
  (permission-resolver/permitted? bond user :permission.bond/modify))

(defn bond-associated-with-workspace?
  [bond workspace]
  (-> (map :db/id (:workspace/bonds workspace))
      set
      (contains? (:db/id bond))))

(defn cancelled-invite?
  [bond]
  (du/in-state? :bond.state/cancelled-invite bond))

(defn bond-owner-name [lang owner]
  (cond
    (eu/is-type? :entity.type/business owner) (business-model/format-name owner lang)
    (eu/is-type? :entity.type/user owner) (user-model/full-name owner)
    :else ""))

(defn claim-task
  "Get the claim-bond-account task for a pending-invite bond."
  [bond]
  (->> bond
       :task/_reference
       (filter #(= :task.type/claim-bond-account (:task/type %)))
       first))

(defn claim-task-email
  "Get the invited email from a pending-invite bond's claim task.
   Returns the email string or nil if not found."
  [bond]
  (-> bond claim-task :task/email))

(defn claim-task-user
  "Get the invited user from a pending-invite bond's claim task.
   Returns the user entity if an existing user was invited, nil otherwise."
  [bond]
  (->> bond claim-task :task/assignees
       (filter #(eu/is-type? :entity.type/user %))
       first))

(defn claim-task-user-group
  "Get the invited business admin group from a pending-invite bond's claim task.
   For business invites the task assignee is the business admin group (not the business entity).
   Returns the group entity or nil."
  [bond]
  (-> bond claim-task :task/user-group))

(defn bond-owner?
  [bond user]
  (let [owner (:bond/owner bond)]
    (cond
      (eu/is-type? :entity.type/user owner)
      (= (:db/id owner) (:db/id user))

      (eu/is-type? :entity.type/business owner)
      (business-model/user-is-member? owner user)

      :else false)))

(def bond-group-config-keys
  "Bond configuration fields stored directly on the domain group.
   These business rules apply to all bonds within the domain."
  #{:group/bond-owner-can-extend?
    :group/bond-owner-can-change-amount?
    :group/bond-require-verification?
    :group/bond-required-fields})

(def valid-bond-required-fields
  "Fields that can be configured as required in domain bond configuration.
   These are optional fields in the Bond schema that can be made mandatory."
  #{:bond/issuer-phone
    :bond/issuer-email})

(def bond-application-required-keys
  "Bond fields required when creating or fully updating a bond application."
  [:bond/owner
   :bond/template
   :bond/external-id
   :bond/amount
   :bond/expires-on
   :bond/description
   :bond/issuer-name])

(def bond-application-optional-keys
  "Bond fields optional when creating or fully updating a bond application."
  [:bond/issuer-phone
   :bond/issuer-email])

(def bond-application-keys
  "All bond fields used in the bond application form."
  (into bond-application-required-keys bond-application-optional-keys))

(def pending-invite-editable-attrs
  "Attributes that can be edited (including retracted) on a pending-invite bond."
  (remove #{:bond/owner} bond-application-keys))

(def bond-application-non-string-keys
  "Bond application fields that should not be coerced to strings in the UI."
  #{:bond/owner
    :bond/template
    :bond/amount
    :bond/expires-on})

(def bond-application-string-keys
  "Bond application fields that are treated as strings in the UI."
  (->> bond-application-keys
       (remove bond-application-non-string-keys)
       set))

(def authority-groups-with-bonds
  "Authority groups/permit types that can use the bond accounts feature"
  #{:permit-type/B     ; Building
    :permit-type/CR    ; Contractor Registration
    :permit-type/P     ; Planning Approval
    :permit-type/LU    ; Land Use
    :permit-type/PW})  ; Public Works

(defmethod du/is-active? :entity.type/bond-template
  [{state :domain/state}]
  (= state :bond-template.state/active))

(defn bonds-using-template
  "Returns all bonds that reference the given bond template"
  [bond-template]
  (:bond/_template bond-template))

(def ^{:arglists '([bond-template lang])} template-name
  (i18n-model/translation-fallback-fn :bond-template/names))

(defn bond-type-name [bond lang]
  (-> bond :bond/template (template-name lang)))

(defn bond-editable-state? [bond group-owner-flag]
  (or (du/in-state? :bond.state/unverified bond)
      (and ((du/in-state-fn :bond.state/verified :bond.state/expired) bond)
           (-> bond :bond/group group-owner-flag))))

(defn owner-can-extend? [bond]
  (bond-editable-state? bond :group/bond-owner-can-extend?))

(defn owner-can-change-amount? [bond]
  (bond-editable-state? bond :group/bond-owner-can-change-amount?))

(def ^{:arglists '([bond])} audit-events
  (child/child-entities-of-type-fn :entity.type/bond-audit-event))

(defn bond-required-for-workspace? [workspace]
  (->> workspace
       workspace-model/default-config-path
       workspace-model/flatten-config-path
       (map :group.config/bond-required?)
       (some true?)))

(def ^{:arglists '([bond])} attachments
  (child/child-entities-of-type-fn :entity.type/attachment))

(defn bond-identifier
  "Returns the display identifier for a bond."
  [bond]
  (or (:bond/external-id bond) ; External-id is optional
      (:bond/id bond)))

(defn bond-admin-group [domain-group]
  (->> domain-group
       group-model/child-groups
       (filter #(group-model/has-role? :group.role/bond-admin %))
       first))

(defn user-is-bond-admin-in-group? [user domain-group]
  (permission-resolver/permitted? domain-group user :permission.bond/admin))

#?(:cljs
   (defn user-is-bond-admin?
     "Note: This function only works on the front end since it relies on :user/domain-groups
      being populated (see `app.domain.user.core/export-authority-data`)"
     [user]
     (->> user
          :user/domain-groups
          (mapcat :group-roles)
          (some #(= % :group.role/bond-admin)))))

(defn notifiable-bond-admins [domain-group notification-type]
  (->> domain-group
       bond-admin-group
       group-model/get-users-in-group
       (filter (fn [user]
                 (and (:user/email user)
                      (contains? (:user/notification-types user) notification-type))))))

(defn external-id-unique-in-domain-group?
  [bond external-id domain-group]
  (let [existing-external-ids (-> (map :bond/external-id (bonds-in-group domain-group))
                                  set
                                  (disj (:bond/external-id bond)))]
    (and (not (str/blank? external-id))
         (not (contains? existing-external-ids external-id)))))

(defn available-bond-attachment-types
  "Get the set of available (enabled) bond attachment type IDs for the group.
   Returns a set of entity IDs."
  [group]
  (into #{} (map :db/id (:group/available-bond-attachments group))))
